import { useIntl } from "react-intl";
import type { FundingApplicationSchemas } from "@wayflyer/api-types";

import type { OnboardingStep } from "./utils";
import { ONBOARDING_STEP_IDS, ONBOARDING_SUBSTEP_IDS, DOCUMENT_TYPE_TO_SUBSTEP } from "./constants";
import { ONBOARDING_WORKFLOW_MESSAGES } from "./messages";

type UseOnboardingStepsOptions = {
  includeBusinessLocation?: boolean;
  stagesConfig?: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen;
  onNavigateToStep: (stepId: string, substepId?: string) => void;
};

export const useOnboardingWorkflowSteps = ({
  includeBusinessLocation = true,
  stagesConfig,
  onNavigateToStep,
}: UseOnboardingStepsOptions): OnboardingStep[] => {
  const intl = useIntl();
  const connectionsSubsteps = buildConnectionsSubsteps(stagesConfig, intl);

  return [
    {
      id: ONBOARDING_STEP_IDS.BUSINESS_COUNTRY,
      label: intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.stepBusinessCountry),
      description: intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.stepBusinessCountryDescription),
      subtext: intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.stepBusinessCountrySubtext),
      onClick: () => onNavigateToStep(ONBOARDING_STEP_IDS.BUSINESS_COUNTRY),
      analyticsId: "onboarding-navigate-business-country",
    },
    {
      id: ONBOARDING_STEP_IDS.MONTHLY_REVENUE,
      label: intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.stepMonthlyRevenue),
      description: intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.stepMonthlyRevenueDescription),
      subtext: intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.stepMonthlyRevenueSubtext),
      onClick: () => onNavigateToStep(ONBOARDING_STEP_IDS.MONTHLY_REVENUE),
      analyticsId: "onboarding-navigate-monthly-revenue",
    },
    {
      id: ONBOARDING_STEP_IDS.BUSINESS_DETAILS,
      label: intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.stepBusinessDetails),
      description: intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.stepBusinessDetailsDescription),
      subtext: intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.stepBusinessDetailsSubtext),
      substeps: [
        ...(includeBusinessLocation
          ? [
              {
                id: ONBOARDING_SUBSTEP_IDS.BUSINESS_LOCATION,
                label: intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.substepBusinessLocation),
                onClick: () =>
                  onNavigateToStep(ONBOARDING_STEP_IDS.BUSINESS_DETAILS, ONBOARDING_SUBSTEP_IDS.BUSINESS_LOCATION),
                analyticsId: "onboarding-navigate-business-location",
              },
            ]
          : []),
        {
          id: ONBOARDING_SUBSTEP_IDS.BUSINESS_INFORMATION,
          label: intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.substepBusinessInformation),
          onClick: () =>
            onNavigateToStep(ONBOARDING_STEP_IDS.BUSINESS_DETAILS, ONBOARDING_SUBSTEP_IDS.BUSINESS_INFORMATION),
          analyticsId: "onboarding-navigate-business-information",
        },
        {
          id: ONBOARDING_SUBSTEP_IDS.BUSINESS_MODEL,
          label: intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.substepBusinessModel),
          onClick: () => onNavigateToStep(ONBOARDING_STEP_IDS.BUSINESS_DETAILS, ONBOARDING_SUBSTEP_IDS.BUSINESS_MODEL),
          analyticsId: "onboarding-navigate-business-model",
        },
      ],
    },
    {
      id: ONBOARDING_STEP_IDS.CONNECTIONS_AND_DATA,
      label: intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.stepConnectionsAndData),
      description: intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.stepConnectionsAndDataDescription),
      subtext: intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.stepConnectionsAndDataSubtext),
      ...(connectionsSubsteps?.length
        ? {
            substeps: connectionsSubsteps.map((sub) => ({
              id: sub.id,
              label: sub.label,
              onClick: () => onNavigateToStep(ONBOARDING_STEP_IDS.CONNECTIONS_AND_DATA, sub.id),
              analyticsId: `onboarding-navigate-${sub.id}`,
            })),
          }
        : {
            onClick: () => onNavigateToStep(ONBOARDING_STEP_IDS.CONNECTIONS_AND_DATA),
            analyticsId: "onboarding-navigate-connections-data",
          }),
    },
  ];
};

type SubstepId = (typeof ONBOARDING_SUBSTEP_IDS)[keyof typeof ONBOARDING_SUBSTEP_IDS];

function buildConnectionsSubsteps(
  stagesConfig: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen | undefined,
  intl: ReturnType<typeof useIntl>
): Array<{ id: SubstepId; label: string }> {
  if (!stagesConfig) {
    return [];
  }

  const substeps: Array<{ id: SubstepId; label: string }> = [];

  if (stagesConfig.banking) {
    substeps.push({
      id: ONBOARDING_SUBSTEP_IDS.BANK_ACCOUNT,
      label: intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.substepBankAccounts),
    });
  }

  if (stagesConfig.includePlatformSelection) {
    substeps.push({
      id: ONBOARDING_SUBSTEP_IDS.PLATFORMS,
      label: intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.substepPlatforms),
    });
  }

  if (stagesConfig.documents && stagesConfig.documents.length > 0) {
    stagesConfig.documents.forEach((doc) => {
      const substepConfig = DOCUMENT_TYPE_TO_SUBSTEP[doc.documentType];
      if (substepConfig && !substeps.some((substep) => substep.id === substepConfig.id)) {
        substeps.push({
          id: substepConfig.id,
          label: intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES[substepConfig.messageKey]),
        });
      }
    });
  }

  if (stagesConfig.creditConsent) {
    substeps.push({
      id: ONBOARDING_SUBSTEP_IDS.SOFT_CREDIT_CHECK,
      label: intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.substepSoftCreditCheck),
    });
  }

  return substeps;
}
